import React, { useState, useContext, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faTimes, faLock } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Contexts
import { ThemeContext } from "../../context/ThemeProvider";
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";

// Components
import ArticleContent from './ArticleContent';
import PremiumDialog from './PremiumDialog';

// Constants
import { BUNDLE_ARTICLES } from "../../../content";

const RelatedStudyArticles = ({ relatedArticles, currentQuestion, hideBookmark = false, isDiagnostic = false }) => {
  const { theme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  const { isSubscribe } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);

  // Memoize articles array to prevent unnecessary re-renders
  const ARTICLES = useMemo(() => {
    return BUNDLE_ARTICLES[selectedCertificate] || [];
  }, [selectedCertificate]);

  // Memoize filtered articles
  const articles = useMemo(() => {
    if (!relatedArticles || !relatedArticles.length) return [];
    return ARTICLES.filter(article => 
      relatedArticles.includes(article.id)
    );
  }, [relatedArticles, ARTICLES]);

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedArticle(null);
  };

  // Helper function to truncate summary
  const truncateSummary = (summary, maxLength = 120) => {
    if (!summary) return '';
    if (summary.length <= maxLength) return summary;
    return summary.substring(0, maxLength).trim() + '...';
  };

  // If no related articles, don't render anything
  if (!relatedArticles || relatedArticles.length === 0 || articles.length === 0) {
    return null;
  }

  return (
    <>
      {/* Related Articles Section */}
      <div className="space-y-6 pt-6">
        <div className="rounded-lg border shadow-sm"
        style={{
          borderColor: theme.gray[200],
        }}>
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 rounded-t-lg"
          style={{
            backgroundColor: theme.neutral.gray[100],
          }}>
            <FontAwesomeIcon 
              icon={faBook} 
              className="w-5 h-5" 
              style={{ color: theme.primary.blue[600] }} 
            />
            <h3 className="font-semibold text-lg"
            style={{
              color: theme.gray[800],
            }}>
              Related Study Articles
            </h3>
          </div>
          
          {/* Content */}
          <div className="px-6 py-4">
            <div className="space-y-3">
              {articles.map((article, index) => {
                const isLocked = !isSubscribe && index > 0;
                const isDiagnosticLocked = isDiagnostic && isLocked;
                return (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-lg border transition-all duration-200 ${isDiagnosticLocked ? '' : 'cursor-pointer hover:shadow-md'}`}
                    style={{
                      borderColor: theme.gray[200],
                      backgroundColor: isDiagnosticLocked ? 'rgba(0,0,0,0.03)' : theme.background?.card,
                      opacity: isLocked && !isDiagnostic ? 0.7 : 1,
                      pointerEvents: isDiagnosticLocked ? 'none' : 'auto',
                    }}
                    onClick={() => isDiagnosticLocked ? null : isLocked ? setShowPremiumDialog(true) : handleArticleClick(article)}
                    whileHover={isDiagnosticLocked ? {} : { scale: 1.02 }}
                    whileTap={isDiagnosticLocked ? {} : { scale: 0.98 }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base mb-2"
                        style={{
                          color: theme.text?.primary,
                          opacity: isDiagnosticLocked ? 0.55 : 1,
                        }}>
                          {article.article_title}
                        </h4>
                        {article.summary && (
                          <p className="text-sm"
                          style={{
                            color: theme.text?.secondary,
                            opacity: isDiagnosticLocked ? 0.55 : 1,
                          }}>
                            {truncateSummary(article.summary)}
                          </p>
                        )}
                      </div>
                      {isLocked && !isDiagnostic && (
                        <FontAwesomeIcon
                          icon={faLock}
                          style={{
                            color: theme?.primary?.orange?.[500],
                            fontSize: '1rem',
                            flexShrink: 0,
                            marginTop: '0.125rem',
                          }}
                        />
                      )}
                      {isDiagnosticLocked && (
                        <span
                          style={{
                            backgroundColor: theme.gray?.[200] || '#e5e7eb',
                            color: theme.gray?.[500] || '#6b7280',
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            padding: '0.1rem 0.4rem',
                            borderRadius: '4px',
                            flexShrink: 0,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            marginTop: '0.2rem',
                          }}
                        >
                          PRO
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Article Dialog */}
      <AnimatePresence>
        {isDialogOpen && selectedArticle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onClick={closeDialog}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg"
              style={{
                background: theme?.background?.card,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Dialog Header */}
              <div className="flex items-center justify-between p-6 border-b"
              style={{
                borderColor: theme?.borders?.primary,
              }}>
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon 
                    icon={faBook} 
                    style={{ color: theme?.primary?.blue?.[600] }} 
                  />
                  <h2 className="text-xl font-semibold"
                  style={{
                    color: theme?.text?.primary,
                  }}>
                    {selectedArticle.article_title}
                  </h2>
                </div>
                <button
                  onClick={closeDialog}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  style={{
                    color: theme?.text?.secondary,
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Dialog Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                <ArticleContent 
                  article={selectedArticle}
                  showHeader={true}
                  showBookmark={!hideBookmark}
                  showStats={true}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Premium Dialog */}
      <PremiumDialog
        isOpen={showPremiumDialog}
        onClose={() => setShowPremiumDialog(false)}
        onUpgrade={() => {
          setShowPremiumDialog(false);
          navigate('/chooseplan');
        }}
        theme={theme}
      />
    </>
  );
};

export default RelatedStudyArticles; 